import { useState } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { ThumbsUp, MessageSquare, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { FeedbackHistory } from "./admin/PreviewBox";

export const PreviewValidation = ({ token, data, onUpdate }) => {
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (decision) => {
    if (sending) return;
    setError("");
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API_BASE}/api/track/${token}/feedback`, { decision, comment });
      onUpdate({ preview_status: decision === "approve" ? "approved" : "changes_requested", feedback: [...(data.feedback || []), res.entry] });
      setMode(null);
      setComment("");
    } catch (e) {
      setError(e.response?.data?.detail || "Envoi impossible. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const approved = data.preview_status === "approved";

  return (
    <section className="mt-10 rounded-3xl bg-navy p-6 text-white sm:p-8" data-testid="tracking-preview">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Votre maquette</p>
      <h2 className="mt-2 font-display text-lg font-bold md:text-lg">
        {approved ? "Merci, votre maquette est approuvée !" : "La première version de votre site vous attend."}
      </h2>
      <a
        data-testid="tracking-preview-link"
        href={data.preview_url}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/20"
      >
        Ouvrir la maquette <ExternalLink size={14} />
      </a>

      {approved ? (
        <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy" data-testid="tracking-approved">
          <CheckCircle2 size={15} /> Nous finalisons la mise en ligne.
        </p>
      ) : mode === "changes" ? (
        <div className="mt-5">
          <textarea
            data-testid="tracking-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="Décrivez les modifications souhaitées : textes, couleurs, photos, sections…"
            className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="tracking-send-comment" onClick={() => submit("changes")} disabled={sending || comment.trim().length < 5} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-all hover:bg-gold-light disabled:opacity-50">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Envoyer mes remarques
            </button>
            <button data-testid="tracking-cancel-comment" onClick={() => setMode(null)} className="rounded-full px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="tracking-approve" onClick={() => submit("approve")} disabled={sending} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-all hover:bg-gold-light disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <ThumbsUp size={14} />} J'approuve la maquette
          </button>
          <button data-testid="tracking-request-changes" onClick={() => setMode("changes")} className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold">
            <MessageSquare size={14} /> Demander des modifications
          </button>
        </div>
      )}
      {error && <p className="mt-3 text-sm text-gold-light" data-testid="tracking-feedback-error">{error}</p>}
      {data.feedback?.length > 0 && (
        <div className="mt-6 border-t border-white/10 pt-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Vos échanges</p>
          <FeedbackHistory feedback={data.feedback} testId="tracking-feedback-history" light />
        </div>
      )}
    </section>
  );
};
